import { TranslateService } from '@ngx-translate/core';
import { createExtendedMesh } from 'src/app/interfaces/shared/extendMesh';
import { GPS } from 'src/app/interfaces/shared/gps';
import * as THREE from 'three';
import * as Tools from '../../../../classes//shared/threeDTools';
import { Model, ModelOrigin } from '../../../../interfaces/api/model';
import { LatLon } from '../../../../interfaces/shared/latLon';
import { Position } from '../../../../interfaces/shared/position';
import { XYZ } from '../../../../interfaces/shared/xyz';
import { LocalPoint, PointKey } from '../../../../interfaces/viewer/origin';
import { ModelService } from '../../../../services/api/models/model.service';

interface CalibratorParams {
    viewer: Autodesk.Viewing.GuiViewer3D;
    model: Model;
    updateOrigin: (latLon: LatLon, projectLatLonXY?: [number, number] | null) => Model;
    modelService: ModelService;
    updateModel: (model: Model) => void;
    onOriginChange: (evt: ModelOrigin, save?: boolean) => void;
    updateOriginPanel: (origin: ModelOrigin) => void;
    translate: TranslateService;
}

export class CalibratorTool {
    localPoints: { [key in PointKey]?: LocalPoint }
    zAxis: number;
    updateOrigin: (latLon: LatLon, projectLatLonXY?: [number, number] | null) => Model;
    undoList: any[] = [];
    calibratorOverlay: { [key: string]: string } = {};
    pointMaterials: { [key: string]: THREE.Material } = {};
    startingSphere: any;
    refMeshes: any;
    sphereRadius!: number;
    toolName: string = "calibrator-tool";
    viewer: Autodesk.Viewing.GuiViewer3D;
    origin: ModelOrigin;
    projectXY: [number, number] | undefined;
    offset: XYZ = {
        x: 0,
        y: 0,
        z: 0
    };
    model: Model;
    modelService: ModelService;
    currentMarkerName: PointKey = "point-1";
    realPoints: { [key in PointKey]?: Position | null } = {};
    scale: number = 1;
    angle: number = 0;
    zAngle: number = 0;
    onOriginChange: (evt: ModelOrigin, save?: boolean) => void;
    updateOriginPanel: (origin: ModelOrigin) => void;
    offsetLabel: any;
    originLabel: any;
    offsetGeometry: any;
    offsetMesh: any;
    initialOrigin: ModelOrigin;
    unitExt: any;
    zoneExt: any;
    calibrated: boolean = false;
    translate: TranslateService;
    pointsValidated: { [key in PointKey]: boolean } = { "point-1": false, "point-2": false, "origin": false, "real": false }; // origin & real need no validation
    z: number = 0;
    initialZ!: number;
    devMode: boolean = false; // Set to true to enable dev mode (and view origin point)
    prevScale: number = 1;
    transform = {
        angle: 0,
        zAngle: 0,
        scale: 0
    }
    pointMeshes: { [key in PointKey]?: THREE.Mesh | null } = {
        "point-1": null,
        "point-2": null
    };
    scaleDivider: number = 50;

    constructor(params: CalibratorParams) {
        this.viewer = params.viewer;
        params.model.origin.markers = params.model.origin.markers ?? [];
        this.origin = this.cloneOrigin(params.model.origin);
        this.initialOrigin = this.cloneOrigin(params.model.origin);
        let offset = this.viewer.model.getData().globalOffset;
        this.offset = offset ?? { x: 0, y: 0, z: 0 };
        this.z = this.offset && this.offset.z >= 80 ? this.offset.z : 80;
        this.projectXY = params.model.origin.projectXY;
        this.viewer = params.viewer;
        this.model = params.model;
        this.localPoints = {};

        this.zAxis = this.origin.position.z;
        this.updateOrigin = params.updateOrigin;
        this.modelService = params.modelService;
        this.onOriginChange = params.onOriginChange;
        this.updateOriginPanel = params.updateOriginPanel;
        this.calibratorOverlay = { "point-1": "point1Overlay", "point-2": "point2Overlay", "offset": "offsetOverlay", "real": "realOverlay" }

        const ForgeTHREE = (window as any).THREE;
        this.pointMaterials = {
            "point-1": new ForgeTHREE.MeshBasicMaterial({ color: 0x0000ff }),
            "point-2": new ForgeTHREE.MeshBasicMaterial({ color: 0xff0000 }),
            "origin": new ForgeTHREE.MeshBasicMaterial({ color: 0x00ff00 }),
            "real": new ForgeTHREE.MeshBasicMaterial({
                color: 0xff0000,
                transparent: true,
                opacity: 0.5
            })
        }

        this.translate = params.translate;
        this.startingSphere = [];
        this.refMeshes = {};
        this.angle = this.origin.angle
        this.zAngle = this.origin.zAngle

        if (params.model && params.model.origin) {
            this.scale = this.origin.scale || 1;
            this.angle = this.origin.angle;
            this.zAngle = this.origin.zAngle;
        }

        this.initLocalPoints();
        let saveBtn = document.getElementById('terminate');

        saveBtn?.addEventListener('click', () => {
            this.setMarkersInOrigin();
            this.origin.scale = this.scale;
            this.model = this.updateOrigin({ lat: this.origin.position.lat, lon: this.origin.position.lon }, this.origin.projectLatLonXY);
            this.onOriginChange(this.origin, false);
            this.updateOriginPanel(this.origin);
            this.model.origin = this.origin;
            this.calibrated = true;
            this.updateModel(this.model);
            let closeBtn = document.querySelector('#calibratorDockingPanel .docking-panel-close') as HTMLButtonElement;
            closeBtn?.click();
        });

        ["point-1", "point-2"].forEach((name: string) => {
            this.initPointAdder(name as PointKey);
        });

        let resetBtn = document.getElementById('reinitialize');
        resetBtn?.addEventListener('click', () => {
            this.reset();
        });

        let calibrateBtn = document.getElementById('calibrate');

        calibrateBtn?.addEventListener('click', () => {
            this.calibrate();
            this.onOriginChange(this.origin, true);
        });

        this.initialZ = this.localPoints["point-1"] ? this.localPoints["point-1"].z : this.z;

        this.animate();
    }

    validate(name: PointKey, bool: boolean = true) {
        this.pointsValidated[name] = bool;
    }

    reset() {
        this.deletePoints();
        this.enableBtns(["reinitialize", "calibrate"], false);
        this.enableBtn("terminate", this.initialOrigin.markers.length > 0);

        this.origin = this.cloneOrigin(this.initialOrigin);
        this.model = this.updateOrigin({ lat: this.origin.position.lat, lon: this.origin.position.lon }, this.origin.projectLatLonXY);

        this.origin.markers = [];
        this.angle = this.origin.angle;
        this.zAngle = this.origin.zAngle;
        this.scale = this.origin.scale;

        this.updateInputValue(this.origin.scale, "model", "scale");
        this.updateInputValue(this.origin.angle, "model", "orientation");
        this.onOriginChange(this.origin, false);

        this.model.origin = this.origin;
        this.currentMarkerName = "point-1";

        ["point-1", "point-2"].forEach(name => {
            this.setState(name, "local");
        });
    }

    initLocalPoints() {
        if (!this.origin.markers || this.origin.markers.length == 0 || !this.origin.markers[0] || this.origin.markers[0].local == undefined) return;

        let point1 = this.origin.markers[0];
        this.localPoints["point-1"] = { ...point1.local, z: point1.local.z ?? this.initialZ };
        this.currentMarkerName = "point-1";

        if (point1.real) {
            this.realPoints["point-1"] = point1.real;
            this.setState("point-1", "real");
            this.updateInput("point-1", "real");
        } else {
            this.updateInput("point-1", "local");
            this.updateRealLatLon("point-1");
        }

        this.drawMark("point-1");

        if (this.localPoints["point-1"]?.x != undefined)
            this.validate("point-1");

        if (this.origin.markers.length > 1) {
            let point2 = this.initialOrigin.markers[1];
            this.localPoints["point-2"] = { ...point2.local, z: point2.local.z ?? this.initialZ };
            this.currentMarkerName = "point-2";

            if (point2.real) {
                this.realPoints["point-2"] = point2.real;
                this.setState("point-2", "real");
                this.updateInput("point-2", "real");
            } else {
                this.updateInput("point-2", "local");
                this.updateRealLatLon("point-2");
            }

            this.drawMark("point-2");

            if (this.localPoints["point-2"]?.x != undefined)
                this.validate("point-2");
        }

        let originXY = Tools.applyTransform2(this.origin.position, this.origin, this.offset, this.scale);
        this.localPoints["origin"] = { ...originXY, z: this.offset.z };
        // this.drawMark("origin");
    }

    public activate(): any {
    }

    public deactivate(): any {
        this.viewer.toolController.deactivateTool(this.toolName);
    }

    haveLatLonChanged(name: PointKey) {
        let localPoint = this.localPoints[name]!;
        let realPoint = this.realPoints[name]!;
        let newRealPoint = Tools.reverseTransform2(
            { x: localPoint.x, y: localPoint.y, z: localPoint.z ?? 0 },
            this.origin,
            this.offset, this.scale);

        if (realPoint.lat.toFixed(7) != newRealPoint.lat.toFixed(7) || realPoint.lon.toFixed(7) != newRealPoint.lon.toFixed(7))
            return true;

        return false;
    }

    hasLocalPointChanged(name: PointKey) {
        let localPoint = this.localPoints[name]!;
        let realPoint = this.realPoints[name]!;
        let newLocalPoint = Tools.applyTransform2(realPoint, this.origin, this.offset, this.scale);

        if (localPoint.x.toFixed(3) != newLocalPoint.x.toFixed(3) || localPoint.y.toFixed(3) != newLocalPoint.y.toFixed(3))
            return true;

        return false;
    }

    calibrate() {
        if (!this.arePoints1And2Set()) return;

        this.computeScale();
        this.computeAngle();
        this.computePosition();

        this.onOriginChange(this.origin, true);
    }

    getMarkerPoints() {
        return this.localPoints;
    };

    getNames() {
        return [this.toolName];
    };

    getName() {
        return this.toolName;
    };

    updateRealLatLon(name: PointKey | null = null) {
        name = (name ?? this.currentMarkerName);// as PointKey;

        this.realPoints[name] = null;
        if (this.localPoints[name] && this.localPoints[name]?.x != undefined) {
            this.realPoints[name] = Tools.reverseTransform2({
                x: this.localPoints[name]?.x!,
                y: this.localPoints[name]?.y!,
                z: this.localPoints[name]?.z ?? this.z
            },
                this.origin,
                this.offset);
        }
        return this.realPoints[name];
    };

    setLocalPoints(XYZ: XYZ, name: PointKey | null = null) {
        name = name ?? this.currentMarkerName;
        this.localPoints[name] = { ...XYZ };
        return this.localPoints[name];
    }

    transformToLocal(latLon: GPS) {
        return { ...Tools.applyTransform2(latLon, this.origin, this.offset) };
    }

    updateModel(model: Model) {
        this.modelService.updateModel(model).subscribe(res => {
        })
    }

    setCurrentMarker(name: PointKey) {
        this.currentMarkerName = name;
    }

    enableBtn(id: string, bool = true) {
        let btn = document.getElementById(id) as HTMLButtonElement;
        if (!btn) return;
        btn.disabled = !bool;
    }

    enableBtns(ids: string[], bool = true) {
        ids.forEach(id => {
            this.enableBtn(id, bool);
        });
    }

    handleSingleClick(event: { canvasX: number; canvasY: number }, button: number) {
        if (button === 0) {
            let name = this.currentMarkerName;
            if (!name) return;
            if (name == "point-1" && this.pointsValidated["point-1"]) return;
            if (name == "point-2" && this.pointsValidated["point-2"]) return;

            this.calibrated = false;
            let state = this.getState(name);
            if (state == "real") return;
            this.enableBtn('terminate');

            const vpVec = this.viewer.impl.clientToViewport(event.canvasX, event.canvasY);
            let point: XYZ = (this.viewer.impl as any).intersectGroundViewport(vpVec);
            // ["x", "y", "z"].forEach(key => {
            //     point[key] = Math.floor(point[key]);
            // });

            this.localPoints[name] = { ...point, z: 80 - this.offset.z };

            this.updateRealLatLon(name);

            this.enableBtn("reinitialize");
            let lonLat = Tools.getLatLonFromXYZ2(this.localPoints[name].x, this.localPoints[name].y, this.localPoints[name].z);
            // Updating real world x, y in realPoints
            (this.localPoints[name] as any).lon = lonLat[0];
            (this.localPoints[name] as any).lat = lonLat[1];
            this.updateInput(name, "local");
            this.drawMark(name);
        }
        return false;
    };

    initPointAdder(name: PointKey = "point-1") {
        let point2Img = document.querySelector(`[id='editor.${name}']`) as HTMLImageElement;

        if (!point2Img) {
            console.error(`CalibratorTool: L'élément #editor.${name} est introuvable dans le DOM !`);
            return;
        }

        let dataName = point2Img.getAttribute("data-name");

        if (this.realPoints[name] && (this.realPoints[name].lat || this.realPoints[name].lat) && (dataName == `add-${name}` || dataName == `validate-${name}`)) {
            point2Img.setAttribute("data-name", `edit-${name}`);
            point2Img.src = '/img/viewer/edit.png';
            (point2Img.parentNode as HTMLButtonElement).title = this.translate.instant("form.edit");
        }
    }

    togglePointAdder(name = "point-2", reset = false) {
        let pointImg = document.querySelector(`[id='editor.${name}']`) as HTMLImageElement;
        let dataName = pointImg.getAttribute("data-name");
        let inputsEmpty = true;
        let showsAddOrEdit = dataName == `add-${name}` || dataName == `edit-${name}`;
        let addTitle = this.translate.instant(name == "point-1" ? 'viewer.add_point' : 'viewer.add_second_point')
        let updateInputs = document.querySelectorAll(`[data-name="${name}"] input.update-pos`) as NodeListOf<HTMLInputElement>;

        updateInputs.forEach(input => {
            if (input.value)
                inputsEmpty = false;
        });

        if (reset) {
            pointImg.setAttribute("data-name", `add-${name}`);
            pointImg.src = '/img/viewer/add.svg';
            (pointImg.parentNode as HTMLButtonElement).title = addTitle;

            ["point-1", "point-2"].forEach(name => {
                this.setState(name, "local");
            });

            return;
        }
        if (showsAddOrEdit) {
            pointImg.setAttribute("data-name", `validate-${name}`);
            pointImg.src = '/img/viewer/ok.png';
            (pointImg.parentNode as HTMLButtonElement).title = this.translate.instant("viewer.validate");
        } else if (dataName == `validate-${name}`) {
            pointImg.setAttribute("data-name", inputsEmpty ? `add-${name}` : `edit-${name}`);
            pointImg.src = `/img/viewer/${inputsEmpty ? "add.svg" : "edit.png"}`;
            (pointImg.parentNode as HTMLButtonElement).title = inputsEmpty ? addTitle : this.translate.instant("form.edit");
        }
    }

    computeScale(localPoints: { [key in PointKey]?: LocalPoint } | null = null, realPoints: { [key in PointKey]?: Position | null } | null = null) {
        if (!localPoints) localPoints = { ...this.localPoints };
        if (!realPoints) realPoints = { ...this.realPoints };

        const deltaLocal = new THREE.Vector3(
            (localPoints["point-2"]!.x - localPoints["point-1"]!.x),
            (localPoints["point-2"]!.y - localPoints["point-1"]!.y),
            0
        );

        // Relative Mercato between 2 points
        const relXY = Tools.getXYFromLatLonAlt2(
            realPoints["point-2"]!.lat, realPoints["point-2"]!.lon,
            0,
            realPoints["point-1"]!.lat, realPoints["point-1"]!.lon
        );
        const deltaReal = new THREE.Vector3(relXY[0] ?? 0, relXY[1] ?? 0, 0);

        const scale = deltaLocal.length() / deltaReal.length();
        this.scale = scale;
        this.transform.scale = scale;
        this.origin.scale = scale;

        this.updateInputValue(scale, "model", "scale");
    }

    setAngle(newAngle: number) {
        const normalizedAngle = this.normalizeAngle(newAngle);
        const delta = normalizedAngle - this.angle;

        const adjustedDelta = ((delta + 540) % 360) - 180;


        this.angle += adjustedDelta;
        this.angle = this.normalizeAngle(this.angle);
        return this.angle;
    }

    normalizeAngle(angle: number): number {
        angle %= 360;

        if (angle <= -180) {
            angle += 360;
        } else if (angle > 180) {
            angle -= 360;
        }

        return angle;
    }

    setRadian(newRadian: number) {
        const normalizedRadian = this.normalizeRadian(newRadian);
        const delta = normalizedRadian - this.zAngle;

        const adjustedDelta = ((delta + Math.PI) % (2 * Math.PI)) - Math.PI;

        this.zAngle += adjustedDelta;
        this.zAngle = this.normalizeRadian(this.zAngle);

        return this.zAngle;
    }

    normalizeRadian(radian: number): number {
        radian %= 2 * Math.PI;

        if (radian <= -Math.PI) {
            radian += 2 * Math.PI;
        } else if (radian > Math.PI) {
            radian -= 2 * Math.PI;
        }

        return radian;
    }

    cloneOrigin(origin: ModelOrigin): ModelOrigin {
        let clonedOrigin: ModelOrigin = {
            ...origin,
            position: { ...origin.position },
            orientation: origin.orientation ? origin.orientation.clone() : origin.orientation,
            markers: [...origin.markers],
            projectLatLonXY: origin.projectLatLonXY ? [...origin.projectLatLonXY] : origin.projectLatLonXY,
            projectXY: origin.projectXY ? [...origin.projectXY] : origin.projectXY
        };

        return clonedOrigin;
    }

    calculateAngle(point1: XYZ, point2: XYZ, point3: XYZ): [number, number] {
        let point1X = point1.x
        let point1Y = point1.y
        let point1Z = point1.z
        let point2X = point2.x
        let point2Y = point2.y
        let point2Z = point2.z

        let vectorPoint1 = new THREE.Vector3(point1X, point1Y, point1Z);
        let vectorPoint2 = new THREE.Vector3(point2X, point2Y, point2Z);
        let vectorPoint3 = new THREE.Vector3(point3.x, point3.y, point3.z);

        const vector1 = vectorPoint2.clone().sub(vectorPoint1).normalize();
        const vector2 = vectorPoint3.clone().sub(vectorPoint1).normalize();

        const dotProduct = vector1.dot(vector2);
        const magnitude1 = vector1.length();
        const magnitude2 = vector2.length();
        const angleRadians = Math.acos(dotProduct / (magnitude1 * magnitude2));
        const angleDegrees = angleRadians * 180 / Math.PI;

        const crossProduct = new THREE.Vector3().crossVectors(vector1, vector2);

        const sign = Math.sign(crossProduct.z);

        return [sign * angleRadians, sign * angleDegrees];
    }

    computeAngle() {
        let localPoints = { ...this.localPoints };
        let realPoints = { ...this.realPoints };

        const localLine = new THREE.Vector3(
            (localPoints["point-2"]!.x - localPoints["point-1"]!.x),
            (localPoints["point-2"]!.y - localPoints["point-1"]!.y),
            0
        ).normalize();

        // Relative Mercato between 2 points
        const realRelXY = Tools.getXYFromLatLonAlt2(
            realPoints["point-2"]!.lat, realPoints["point-2"]!.lon,
            0,
            realPoints["point-1"]!.lat, realPoints["point-1"]!.lon
        );
        const realLine = new THREE.Vector3(realRelXY[0] ?? 0, realRelXY[1] ?? 0, 0).normalize();

        const angleRadian = Math.atan2(localLine.y, localLine.x) - Math.atan2(realLine.y, realLine.x);
        const angleDegrees = angleRadian * 180 / Math.PI;
        this.origin.angle = this.angle = angleDegrees;
        this.origin.zAngle = this.zAngle = angleRadian;
        this.transform.angle = this.angle;
        this.transform.zAngle = this.zAngle;
        this.updateInputValue(this.origin.angle, "model", "orientation");
    }

    getInputValue(type: string = "position.lat", name: string = "point-1"): number {
        let input = document.querySelector(`[id="${type}.${name}"]`) as HTMLInputElement;
        let value = input.value ? parseFloat(input.value) * 1 : 0;

        return value;
    }

    updateInputValue(value: string | number, type = "position.lat", name = "point-1") {
        let input = document.querySelector(`[id="${type}.${name}"]`) as HTMLInputElement;

        if (!input) {
            return
        }

        input.value = String(value);
        return input;
    }

    drawMark(name: PointKey | null = null) {
        name = name ?? this.currentMarkerName;

        if ((name == "origin") && !this.devMode)
            return;

        if (!this.localPoints[name])
            return;

        if (this.refMeshes[name]) {
            this.undoMark(name);
        }

        let material = this.pointMaterials[name];
        if (!material) return;

        const camera: any = this.viewer.getCamera();
        let scale = 1;

        const ForgeTHREE = (window as any).THREE;
        let sphereGeometry = new ForgeTHREE.SphereGeometry(1, 16, 16);
        this.pointMeshes[name] = createExtendedMesh(sphereGeometry, material.clone());

        this.pointMeshes[name]!.name = name;
        this.pointMeshes[name]!.position.set(
            this.localPoints[name]!.x,
            this.localPoints[name]!.y,
            this.localPoints[name]!.z ?? this.offset.z
        );

        let dist = this.pointMeshes[name]!.position.distanceTo(camera.position);
        scale = dist / this.scaleDivider;
        this.pointMeshes[name]!.scale.set(scale, scale, scale);

        this.viewer.impl.createOverlayScene(this.calibratorOverlay[name]!, material);
        this.viewer.impl.addOverlay(this.calibratorOverlay[name]!, this.pointMeshes[name]!);

        this.refMeshes[name] = this.pointMeshes[name];
        this.viewer.impl.invalidate(true, true, true);
    }


    undoMark(name: PointKey) {
        var meshRef = this.refMeshes[name];

        if (!meshRef) return;

        if (meshRef) {
            this.viewer.impl.removeOverlayScene(this.calibratorOverlay[name]!);
            this.viewer.impl.removeOverlay(this.calibratorOverlay[name]!, meshRef);
        }

        this.viewer.impl.invalidate(true, true, true);
        this.refMeshes[name] = undefined;
    }

    removeMarks() {
        ["point-1", "point-2", "real"].forEach(name => {
            this.undoMark(name as PointKey);
        });
    }

    onInputChange(e: Event) {
        this.enableBtns(['calibrate'], true);
        this.calibrated = false;
        let diff = 0;
        let input = e.target as HTMLInputElement
        let value: number = parseFloat(input.value);
        let id = input.getAttribute('id');
        let type = input.getAttribute('type');
        let name = this.currentMarkerName;
        let state = this.getState(name);
        let isLocal = state == "local";
        let isReal = state == "real";

        if (!this.localPoints[name])
            this.localPoints[name] = { x: 0, y: 0, z: this.z };

        if (!this.realPoints[name]) {
            if (isReal) {
                this.updateRealLatLon(name);
                this.updateInput(name, "real");
            }
            else
                this.realPoints[name] = { lat: 0, lon: 0, z: this.z, x: 0, y: 0 };
        }

        let attrName = id?.split(".")[0];
        let latOrLon = id?.split(".")[1]!;

        let otherLatOrLon = latOrLon == "lat" ? "lon" : "lat";

        if (isLocal) {
            latOrLon = latOrLon == "lat" ? "x" : "y";
            otherLatOrLon = latOrLon == "x" ? "y" : "x";
        }

        this.enableBtn("reinitialize");

        if (attrName != "position") {
            let z = value;
            let prevValue = isLocal ? (this.localPoints[name].z ?? this.z) : (this.realPoints[name]!.z ?? this.z);
            diff = z - prevValue;

            if (isLocal)
                this.localPoints[name].z += diff;
            else
                this.realPoints[name]!.z += diff;

            this.drawMark(name as PointKey);

            if (isReal && name == "point-1") {
                this.origin.position.z += diff;
                const xyResult = Tools.getXYFromLatLonAlt2(this.origin.position.lat, this.origin.position.lon, this.origin.position.z);
                this.origin.projectLatLonXY = [xyResult[0], xyResult[1]] as [number, number];
                this.onOriginChange(this.origin, false);
                this.enableBtn('terminate');
            }

            if (this.arePoints1And2Set() && this.realPoints["point-1"] && this.realPoints["point-1"].x != undefined) {
                this.enableBtn('calibrate');
            }

            return;
        }

        let point = isLocal ? this.localPoints[name]! : this.realPoints[name]!;
        let prevValue = point[latOrLon as keyof (LocalPoint | Position)];

        diff = value - prevValue!;

        (point as any)[latOrLon] += diff;
        let otherId = "";

        if (isReal)
            otherId = `position.${latOrLon == "lat" ? "lon" : "lat"}.${name}`;
        else
            otherId = `position.${latOrLon == "x" ? "lon" : "lat"}.${name}`;

        let otherInput = document.getElementById(otherId) as HTMLInputElement;
        let otherValue = parseFloat(otherInput.value);

        if (!otherValue || isNaN(otherValue)) {
            if (!point) return;

            otherValue = (point as any)[otherLatOrLon];
        }

        this.enableBtn('terminate');

        if (isLocal) {
            this.processXYInput(name, latOrLon, [value, otherValue]);
            this.drawMark(name);
        }

        if (isReal)
            this.processLatLonInput(name, latOrLon, [value, otherValue]);
    }

    processXYInput(name: PointKey, xOrY: string, xOrYValues: [number, number]) {
        let [value, otherValue] = xOrYValues;
        let XY: Record<string, number> = xOrY == "x" ? { x: value, y: otherValue } : { y: value, x: otherValue };

        this.localPoints[name]!.x = XY["x"] as number;
        this.localPoints[name]!.y = XY["y"] as number;

        if (this.arePoints1And2Set()) {
            this.enableBtn('calibrate');
        }
    }

    processLatLonInput(name: PointKey, latOrLon: string, latLonValues: [number, number]) {
        let [value, otherValue] = latLonValues;
        let latLon: Record<string, number> = latOrLon == "lat" ? { lat: value, lon: otherValue } : { lon: value, lat: otherValue };

        this.realPoints[name]!.lat = latLon["lat"] as number;
        this.realPoints[name]!.lon = latLon["lon"] as number;
        let XY = Tools.getXYFromLatLonAlt2(latLon["lat"] as number, latLon["lon"] as number);

        this.realPoints[name]!.x = XY[0] as number;
        this.realPoints[name]!.y = XY[1] as number;

        if (name == "point-1") {
            this.computePosition();
        }

        if (this.arePoints1And2Set()) {
            this.enableBtn('calibrate');
        }

        if (name == "point-1")
            this.onOriginChange(this.origin, false);
    }

    arePoints1And2Set() {
        let pointsAreSet = true;

        ["point-1", "point-2"].forEach(name => {
            const localOk = !!this.localPoints[name as PointKey] && this.localPoints[name as PointKey]!.x != undefined && this.localPoints[name as PointKey]!.y != undefined;
            const realPosOk = !!this.realPoints[name as PointKey] && this.realPoints[name as PointKey]!.x != undefined && this.realPoints[name as PointKey]!.y != undefined;
            const realGpsOk = !!this.realPoints[name as PointKey] && this.realPoints[name as PointKey]!.lat != undefined && this.realPoints[name as PointKey]!.lon != undefined;

            if (!localOk) 
                console.warn(`❌ ${name}: missing local coordinates`);

            if (!realPosOk) 
                console.warn(`❌ ${name}: missing real position (x,y)`);

            if (!realGpsOk) 
                console.warn(`❌ ${name}: missing GPS (lat,lon)`);

            pointsAreSet = pointsAreSet && localOk && realPosOk && realGpsOk;
        });

        if (pointsAreSet) 
            console.log('✅ All points are properly set');

        return pointsAreSet;
    }

    computePosition() {
        let name: PointKey = "point-1";
        let scale = this.transform.scale || 1;
        let angle = this.transform.zAngle;

        let vec3 = new THREE.Vector3(
            this.localPoints[name]!.x,
            this.localPoints[name]!.y,
            this.localPoints[name]!.z ?? 0
        );

        const cosA = Math.cos(-angle);
        const sinA = Math.sin(-angle);
        const rx = vec3.x * cosA - vec3.y * sinA;
        const ry = vec3.x * sinA + vec3.y * cosA;
        vec3.x = rx;
        vec3.y = ry;
        vec3.x /= scale;
        vec3.y /= scale;

        // Relative Mercator delta from origin to this GPS point
        const R = 6378137;
        const lat_p = this.realPoints[name]!.lat;
        const lon_p = this.realPoints[name]!.lon;

        const origin_lat = lat_p - Tools.radToDeg(vec3.y / R);
        const origin_lat_rad = Tools.degToRad(origin_lat);
        const origin_lon = lon_p - Tools.radToDeg(vec3.x / (R * Math.cos(origin_lat_rad)));

        this.origin.position.lat = origin_lat;
        this.origin.position.lon = origin_lon;
        this.origin.position.z = this.realPoints[name]!.z ?? 0;

        this.origin.projectXY = [0, 0];
        this.origin.projectLatLonXY = [0, 0];
    }

    setMarkersInOrigin() {
        if (this.pointsValidated["point-1"] && this.localPoints["point-1"])
            this.origin.markers[0] = { local: this.localPoints["point-1"], real: this.realPoints["point-1"] };

        if (this.pointsValidated["point-2"] && this.localPoints["point-2"]) {
            let length = this.origin.markers.length;

            if (length > 1)
                this.origin.markers.pop();

            if (this.localPoints["point-2"] && length > 0)
                this.origin.markers[1] = { local: this.localPoints["point-2"], real: this.realPoints["point-2"] };
        }
    }

    deletePoints() {
        this.removeMarks();

        (["point-1", "point-2"] as PointKey[]).forEach(name => {
            this.localPoints[name] = undefined;
            this.realPoints[name] = undefined;

            this.updateInputValue("", "position.lat", name);
            this.updateInputValue("", "position.lon", name);

            this.pointsValidated[name] = true;

            if (name == "point-1")
                this.updateInputValue("", "altitude", name);

            this.togglePointAdder(name, true);
        });
    }

    setState(dataName = "point-1", state: "local" | "real") {
        let latLonLi = document.querySelector(`[data-name="${dataName}"]`);

        if (!latLonLi) return;

        latLonLi.setAttribute("data-state", state);
        let spans = document.querySelectorAll(`li[data-name="label-${dataName}"] span`) as NodeListOf<HTMLSpanElement>;
        let labels: Record<string, string> = {};

        for (let i = 0; i < spans.length; i++) {
            if (!spans || !spans[i]) continue;

            spans[i]!.classList.remove(`label-${state === "local" ? "real" : "local"}`);
            spans[i]!.classList.add(`label-${state}`);
            const attrValue = spans[i]!.getAttribute(`data-${state}`) || '';
            spans[i]!.innerHTML = labels[`data-${state}-${spans[i]!.getAttribute("data-type")}`] = attrValue;

            if (i == 0)
                (spans[i]!.parentNode as HTMLLIElement).setAttribute("data-state", state);
        };

        let inputs = latLonLi.children;

        for (let i = 0; i < inputs.length; i++) {
            if (inputs[i]!.tagName != "INPUT") continue;

            const input = inputs[i] as HTMLInputElement;
            let type = input.id.split(".")[1];
            type = type == "point-1" ? "altitude" : type; // point-1 z id is not the same as others
            input.setAttribute("placeholder", labels[`data-${state}-${type}`] || '');

            if (type != "altitude")
                input.setAttribute("step", state == "real" ? "0.00001" : "0.1");
        }
    }

    getState(dataName = "point-1") {
        let btn = document.querySelector(`[data-name="${dataName}"]`);

        if (!btn) return null;

        return btn.getAttribute("data-state");
    }

    // Update input values from realPoints[name]
    updateInput(name: PointKey, state: "local" | "real") {
        if (!this.localPoints[name]) return;

        let inputId = name == "real" ? "point-2" : name;

        if (state == "real") {
            if (!this.realPoints[name]) this.updateRealLatLon(name);
            this.updateInputValue(this.realPoints[name]!.lat, "position.lat", inputId);
            this.updateInputValue(this.realPoints[name]!.lon, "position.lon", inputId);
            this.updateInputValue(this.realPoints[name]!.z ?? 0, "altitude", inputId);
        }

        if (state == "local") {
            this.updateInputValue(this.localPoints[name]!.x, "position.lat", inputId);
            this.updateInputValue(this.localPoints[name]!.y!, "position.lon", inputId);
            this.updateInputValue(this.localPoints[name]!.z ?? 0, "altitude", inputId);
        }
    }

    animate() {
        if (!this.viewer || !this.viewer.impl) return;

        const camera: any = this.viewer.getCamera();
        let scale = 1;

        Object.keys(this.pointMeshes).forEach(key => {
            const mesh = this.pointMeshes[key as PointKey];

            if (mesh) {
                let dist = mesh.position.distanceTo(camera.position);
                scale = dist / this.scaleDivider;
                mesh.scale.set(scale, scale, scale);
                mesh.updateMatrix();
            }
        })

        requestAnimationFrame(() => this.animate());
    }
}